/**
 * ============================================================================
 * Tooling 模块 API 工厂函数（模式一：单一资源）
 * ============================================================================
 * 
 * 📋 文件职责：
 * - 定义 Tooling（工装管理）模块的所有 API 请求方法
 * - 包含：Tool（刀具）、Gauge（检具）、Fixture（夹具）、Mold（模具）
 * - 通过工厂模式实现请求客户端的依赖注入
 * - 提供完整的 TypeScript 类型安全
 * 
 * 🎯 核心概念：工厂模式
 * - 工厂函数：createToolingApi(client) - 接收请求客户端，返回 API 方法集合
 * - 依赖注入：不依赖具体的请求实现（axios、uni.request 等）
 * - 平台无关：Web 端和 App 端都可以使用，只需提供不同的适配器
 * 
 * 💡 设计理念：
 * - "给我一个能发送请求的工具，我就能为你生成所有 API 方法"
 * - 请求客户端是"工具"，API 方法是"产品"，工厂函数是"生产线"
 * 
 * @module api/tooling/factory
 * @description Tooling（工装管理）模块的 API 工厂函数
 * @generated 2025-11-01
 * @author AI Assistant
 * ============================================================================
 */

// ============================================================================
// 类型导入
// ============================================================================

import type {
  Tool,
  Gauge,
  GaugeCalibrationLog,
  GaugeInspectionRecord,
  Fixture,
  Mold,
  FixtureUsageLog,
  FixtureMaintenanceLog,
  ToolingUsageApplication,
  ToolingAlert,
  ToolingDashboard,
  ToolQueryParams,
  GaugeQueryParams,
  FixtureQueryParams,
  MoldQueryParams,
  CreateToolForm,
  UpdateToolForm,
  CreateGaugeForm,
  UpdateGaugeForm,
  CreateFixtureForm,
  UpdateFixtureForm,
  CreateMoldForm,
  UpdateMoldForm,
  StartUseForm,
  ReturnUseForm,
  MaintenanceForm,
  CreateApplicationForm,
  ReturnCheckoutForm,
  CreateGaugeCalibrationForm,
  HandleAlertForm,
  ReplaceToolForm,
  BorrowGaugeForm,
  MountGaugeForm,
  GaugeBluetoothBinding,
  GaugeBluetoothBindingLog,
  GaugeBluetoothBindingQueryParams,
  BindGaugeBluetoothForm,
  RebindGaugeBluetoothForm,
  UnbindGaugeBluetoothForm,
  GaugeFingerprintProfile,
  GaugeFingerprintSample,
  GaugeFingerprintProfileQueryParams,
  GaugeFingerprintSampleQueryParams,
  CreateGaugeFingerprintProfileForm,
  UpdateGaugeFingerprintProfileForm,
  CaptureGaugeFingerprintSampleForm,
  PromoteGaugeFingerprintSampleForm,
  BatchImportToolItem,
  BatchImportFixtureItem,
  BatchImportMoldItem,
  BatchImportGaugeItem,
} from '../../types/tooling'

import type { PaginationResult, BatchImportResult } from '../../types/api'
import type { IRequestClient } from '../core/client'
import { API_ROUTES } from '../routes'

// ============================================================================
// Tooling API 工厂函数
// ============================================================================

/**
 * 创建 Tooling 模块的 API 方法集合
 */
export function createToolingApi(client: IRequestClient) {
  return {
    // ============================================================================
    // Tool（刀具管理）API 方法
    // ============================================================================
    
    /**
     * 获取刀具列表（分页）
     */
    getToolList(params?: ToolQueryParams) {
      return client.get<PaginationResult<Tool>>(
        API_ROUTES.tooling.toolList,
        { params }
      )
    },
    
    /**
     * 获取刀具详情
     */
    getToolDetail(id: number) {
      return client.get<Tool>(API_ROUTES.tooling.toolDetail(id))
    },
    
    /**
     * 创建刀具
     */
    createTool(data: CreateToolForm) {
      return client.post<Tool>(API_ROUTES.tooling.toolCreate, data)
    },
    
    /**
     * 更新刀具
     */
    updateTool(id: number, data: UpdateToolForm) {
      return client.put<Tool>(API_ROUTES.tooling.toolUpdate(id), data)
    },
    
    /**
     * 删除刀具
     */
    deleteTool(id: number) {
      return client.delete<void>(API_ROUTES.tooling.toolDelete(id))
    },

    /**
     * 更换刀具（重置累计使用量，剩余寿命由后端重算）
     */
    replaceTool(id: number, data: ReplaceToolForm) {
      return client.post<Tool>(API_ROUTES.tooling.toolReplace(id), data)
    },
    
    // ============================================================================
    // Gauge（检具管理）API 方法
    // ============================================================================
    
    /**
     * 获取检具列表（分页）
     */
    getGaugeList(params?: GaugeQueryParams) {
      return client.get<PaginationResult<Gauge>>(
        API_ROUTES.tooling.gaugeList,
        { params }
      )
    },
    
    /**
     * 获取检具详情
     */
    getGaugeDetail(id: number) {
      return client.get<Gauge>(API_ROUTES.tooling.gaugeDetail(id))
    },
    
    /**
     * 创建检具
     */
    createGauge(data: CreateGaugeForm) {
      return client.post<Gauge>(API_ROUTES.tooling.gaugeCreate, data)
    },
    
    /**
     * 更新检具
     */
    updateGauge(id: number, data: UpdateGaugeForm) {
      return client.put<Gauge>(API_ROUTES.tooling.gaugeUpdate(id), data)
    },
    
    /**
     * 删除检具
     */
    deleteGauge(id: number) {
      return client.delete<void>(API_ROUTES.tooling.gaugeDelete(id))
    },

    /**
     * 借用检具
     */
    borrowGauge(id: number, data: BorrowGaugeForm) {
      return client.post<Gauge>(API_ROUTES.tooling.gaugeBorrow(id), data)
    },

    /**
     * 归还检具
     */
    returnGauge(id: number) {
      return client.post<Gauge>(API_ROUTES.tooling.gaugeReturn(id))
    },

    /**
     * 检具上机
     */
    mountGauge(id: number, data: MountGaugeForm) {
      return client.post<Gauge>(API_ROUTES.tooling.gaugeMount(id), data)
    },

    /**
     * 检具下机
     */
    unmountGauge(id: number) {
      return client.post<Gauge>(API_ROUTES.tooling.gaugeUnmount(id))
    },

    /**
     * 检具关联的检验记录（测量/检验报告反查，按检验时间倒序）
     */
    getGaugeInspectionRecords(id: number, params?: any) {
      return client.get<PaginationResult<GaugeInspectionRecord>>(
        API_ROUTES.tooling.gaugeInspectionRecords(id),
        { params }
      )
    },

    // ----------------------------------------------------------------------------
    // 检具蓝牙绑定（一检具一配对，禁止多绑）
    // ----------------------------------------------------------------------------

    /**
     * 获取检具蓝牙绑定列表（分页）
     */
    getGaugeBluetoothBindings(params?: GaugeBluetoothBindingQueryParams) {
      return client.get<PaginationResult<GaugeBluetoothBinding>>(
        API_ROUTES.tooling.gaugeBluetoothBindingList,
        { params }
      )
    },

    /**
     * 登记绑定：把扫描到的蓝牙设备与台账检具做一对一绑定
     */
    createGaugeBluetoothBinding(data: BindGaugeBluetoothForm) {
      return client.post<GaugeBluetoothBinding>(
        API_ROUTES.tooling.gaugeBluetoothBindingCreate,
        data
      )
    },

    /**
     * 换绑（特殊权限 tooling:gauge:bluetooth-rebind）
     */
    rebindGaugeBluetooth(id: number, data: RebindGaugeBluetoothForm) {
      return client.post<GaugeBluetoothBinding>(
        API_ROUTES.tooling.gaugeBluetoothBindingRebind(id),
        data
      )
    },

    /**
     * 解绑（特殊权限 tooling:gauge:bluetooth-rebind）
     */
    unbindGaugeBluetooth(id: number, data: UnbindGaugeBluetoothForm) {
      return client.post<GaugeBluetoothBinding>(
        API_ROUTES.tooling.gaugeBluetoothBindingUnbind(id),
        data
      )
    },

    /**
     * 检具蓝牙绑定日志（换绑/解绑留痕；按检具查，解绑后仍可追溯）
     */
    getGaugeBluetoothBindingLogs(gaugeId: number, params?: any) {
      return client.get<PaginationResult<GaugeBluetoothBindingLog>>(
        API_ROUTES.tooling.gaugeBluetoothBindingLogs(gaugeId),
        { params }
      )
    },

    // ----------------------------------------------------------------------------
    // 检具特征档案与指纹采集（识别能力）
    // 查看走 tooling:gauge:view，采集/固化/维护走 tooling:gauge:update
    // ----------------------------------------------------------------------------

    /**
     * 特征档案列表（扫描页取 is_enabled=true 作为识别依据）
     */
    getGaugeFingerprintProfiles(params?: GaugeFingerprintProfileQueryParams) {
      return client.get<PaginationResult<GaugeFingerprintProfile>>(
        API_ROUTES.tooling.gaugeFingerprintProfileList,
        { params }
      )
    },

    /**
     * 登记特征档案（新增检具型号：加一条档案数据）
     */
    createGaugeFingerprintProfile(data: CreateGaugeFingerprintProfileForm) {
      return client.post<GaugeFingerprintProfile>(
        API_ROUTES.tooling.gaugeFingerprintProfileCreate,
        data
      )
    },

    /**
     * 更新特征档案（未提交字段保持原值）
     */
    updateGaugeFingerprintProfile(id: number, data: UpdateGaugeFingerprintProfileForm) {
      return client.put<GaugeFingerprintProfile>(
        API_ROUTES.tooling.gaugeFingerprintProfileUpdate(id),
        data
      )
    },

    /**
     * 删除特征档案（内置档案不允许删除，只能停用）
     */
    deleteGaugeFingerprintProfile(id: number) {
      return client.delete(API_ROUTES.tooling.gaugeFingerprintProfileDelete(id))
    },

    /**
     * 指纹采集样本列表（待固化优先）
     */
    getGaugeFingerprintSamples(params?: GaugeFingerprintSampleQueryParams) {
      return client.get<PaginationResult<GaugeFingerprintSample>>(
        API_ROUTES.tooling.gaugeFingerprintSampleList,
        { params }
      )
    },

    /**
     * 采集指纹（连接成功后一键采集；同指纹重复采集只累加次数）
     */
    captureGaugeFingerprintSample(data: CaptureGaugeFingerprintSampleForm) {
      return client.post<GaugeFingerprintSample>(
        API_ROUTES.tooling.gaugeFingerprintSampleCapture,
        data
      )
    },

    /**
     * 把采集样本固化为特征档案
     */
    promoteGaugeFingerprintSample(id: number, data: PromoteGaugeFingerprintSampleForm) {
      return client.post<GaugeFingerprintProfile>(
        API_ROUTES.tooling.gaugeFingerprintSamplePromote(id),
        data
      )
    },

    /**
     * 忽略样本（确认不是检具）
     */
    ignoreGaugeFingerprintSample(id: number, data?: { remark?: string | null }) {
      return client.post<GaugeFingerprintSample>(
        API_ROUTES.tooling.gaugeFingerprintSampleIgnore(id),
        data ?? {}
      )
    },

    /**
     * 删除采集样本（已固化的样本不允许删除）
     */
    deleteGaugeFingerprintSample(id: number) {
      return client.delete(API_ROUTES.tooling.gaugeFingerprintSampleDelete(id))
    },
    
    // ============================================================================
    // Fixture（夹具管理）API 方法
    // ============================================================================
    
    /**
     * 获取夹具列表（分页）
     */
    getFixtureList(params?: FixtureQueryParams) {
      return client.get<PaginationResult<Fixture>>(
        API_ROUTES.tooling.fixtureList,
        { params }
      )
    },
    
    /**
     * 获取夹具详情
     */
    getFixtureDetail(id: number) {
      return client.get<Fixture>(API_ROUTES.tooling.fixtureDetail(id))
    },
    
    /**
     * 创建夹具
     */
    createFixture(data: CreateFixtureForm) {
      return client.post<Fixture>(API_ROUTES.tooling.fixtureCreate, data)
    },
    
    /**
     * 更新夹具
     */
    updateFixture(id: number, data: UpdateFixtureForm) {
      return client.put<Fixture>(API_ROUTES.tooling.fixtureUpdate(id), data)
    },
    
    /**
     * 删除夹具
     */
    deleteFixture(id: number) {
      return client.delete<void>(API_ROUTES.tooling.fixtureDelete(id))
    },
    
    /**
     * 开始使用夹具
     */
    startFixtureUse(id: number, data: StartUseForm) {
      return client.post<FixtureUsageLog>(API_ROUTES.tooling.fixtureUse(id), data)
    },
    
    /**
     * 归还夹具
     */
    returnFixture(id: number, data: ReturnUseForm) {
      return client.post<FixtureUsageLog>(API_ROUTES.tooling.fixtureReturn(id), data)
    },
    
    /**
     * 获取夹具使用历史
     */
    getFixtureUsageLogs(id: number, params?: any) {
      return client.get<PaginationResult<FixtureUsageLog>>(
        API_ROUTES.tooling.fixturesUsageLogs(id),
        { params }
      )
    },
    
    /**
     * 执行夹具维护
     */
    performFixtureMaintenance(id: number, data: MaintenanceForm) {
      return client.post<FixtureMaintenanceLog>(API_ROUTES.tooling.fixtureMaintain(id), data)
    },
    
    /**
     * 获取夹具维护历史
     */
    getFixtureMaintenanceLogs(id: number, params?: any) {
      return client.get<PaginationResult<FixtureMaintenanceLog>>(
        API_ROUTES.tooling.fixturesMaintenanceLogs(id),
        { params }
      )
    },
    
    // ============================================================================
    // Mold（模具管理）API 方法
    // ============================================================================
    
    /**
     * 获取模具列表（分页）
     */
    getMoldList(params?: MoldQueryParams) {
      return client.get<PaginationResult<Mold>>(
        API_ROUTES.tooling.moldList,
        { params }
      )
    },
    
    /**
     * 获取模具详情
     */
    getMoldDetail(id: number) {
      return client.get<Mold>(API_ROUTES.tooling.moldDetail(id))
    },
    
    /**
     * 创建模具
     */
    createMold(data: CreateMoldForm) {
      return client.post<Mold>(API_ROUTES.tooling.moldCreate, data)
    },
    
    /**
     * 更新模具
     */
    updateMold(id: number, data: UpdateMoldForm) {
      return client.put<Mold>(API_ROUTES.tooling.moldUpdate(id), data)
    },
    
    /**
     * 删除模具
     */
    deleteMold(id: number) {
      return client.delete<void>(API_ROUTES.tooling.moldDelete(id))
    },
    
    /**
     * 录入检具校准记录（后端自动回填上次/下次校准日期并自愈状态）
     */
    createGaugeCalibration(id: number, data: CreateGaugeCalibrationForm) {
      return client.post<GaugeCalibrationLog>(API_ROUTES.tooling.gaugeCalibrations(id), data)
    },

    // ============================================================================
    // 工装看板 API 方法
    // ============================================================================
    
    /**
     * 获取工装管理看板数据
     */
    getToolingDashboard() {
      return client.get<ToolingDashboard>(API_ROUTES.tooling.dashboard)
    },
    
    // ============================================================================
    // 工装领用与归还 API 方法（2026-09 领用化：无审批环节）
    // ============================================================================

    /**
     * 获取领用记录列表
     */
    getApplications(params?: any) {
      return client.get<PaginationResult<ToolingUsageApplication>>(
        API_ROUTES.tooling.applications,
        { params }
      )
    },

    /**
     * 领用工装
     */
    createApplication(data: CreateApplicationForm) {
      return client.post<ToolingUsageApplication>(API_ROUTES.tooling.applications, data)
    },

    /**
     * 获取领用记录详情
     */
    getApplicationDetail(id: number) {
      return client.get<ToolingUsageApplication>(API_ROUTES.tooling.detail(id))
    },

    /**
     * 归还工装
     */
    returnApplication(id: number, data: ReturnCheckoutForm) {
      return client.post<ToolingUsageApplication>(
        API_ROUTES.tooling.returnApplication(id),
        data
      )
    },

    /**
     * 撤销领用
     *
     * 业务规则：
     * - 只有领用人自己可以撤销
     * - 只有领用中的记录可以撤销
     */
    cancelApplication(id: number) {
      return client.post<ToolingUsageApplication>(
        API_ROUTES.tooling.cancelApplication(id)
      )
    },
    
    // ============================================================================
    // 工装预警管理 API 方法
    // ============================================================================
    
    /**
     * 获取预警列表
     */
    getAlerts(params?: any) {
      return client.get<PaginationResult<ToolingAlert>>(
        API_ROUTES.tooling.alerts,
        { params }
      )
    },
    
    /**
     * 获取未读预警
     */
    getUnreadAlerts(params?: any) {
      return client.get<ToolingAlert[]>(
        API_ROUTES.tooling.unreadAlerts,
        { params }
      )
    },
    
    /**
     * 手动检查预警
     */
    checkAlerts() {
      return client.post<{ count: number }>(API_ROUTES.tooling.checkAlerts)
    },
    
    /**
     * 处理预警
     */
    handleAlert(id: number, data: HandleAlertForm) {
      return client.post<ToolingAlert>(
        API_ROUTES.tooling.handleAlert(id),
        data
      )
    },
    
    /**
     * 忽略预警
     */
    ignoreAlert(id: number) {
      return client.post<ToolingAlert>(API_ROUTES.tooling.ignoreAlert(id))
    },
    
    // ============================================================================
    // 批量导入 API 方法
    // ============================================================================
    
    /**
     * 批量导入刀具
     */
    batchImportTools(tools: BatchImportToolItem[]) {
      return client.post<BatchImportResult>(API_ROUTES.tooling.import, {
        tools,
      })
    },
    
    /**
     * 批量导入夹具
     */
    batchImportFixtures(fixtures: BatchImportFixtureItem[]) {
      return client.post<BatchImportResult>(API_ROUTES.tooling.fixturesImport, {
        fixtures,
      })
    },
    
    /**
     * 批量导入模具
     */
    batchImportMolds(molds: BatchImportMoldItem[]) {
      return client.post<BatchImportResult>(API_ROUTES.tooling.moldsImport, {
        molds,
      })
    },
    
    /**
     * 批量导入检具
     */
    batchImportGauges(gauges: BatchImportGaugeItem[]) {
      return client.post<BatchImportResult>(API_ROUTES.tooling.gaugesImport, {
        gauges,
      })
    },
  }
}

